import { Participant, VideoPlayer as VideoPlayerType } from '@zoom/videosdk';
import { useAnalytics } from 'apps/agora/src/contexts/AnalyticsContext';
import { ANALYTICS_EVENT_NAMES } from 'apps/agora/src/utils/constants';
import { RefObject, useContext, useEffect, useState } from 'react';
import { ZoomContext } from '../contexts/ZoomContext';
import { excludeRecorder } from '../utils/helpers';
import { logger } from '../../../utils/logger';

interface UsePiPProps {
  participants: Participant[];
  videoListRef: RefObject<Record<string, VideoPlayerType>>;
}

const usePiP = (props: UsePiPProps) => {
  const { participants, videoListRef } = props;
  const [participantInPiP, setParticipantInPiP] = useState<string>();

  const { zoomClient } = useContext(ZoomContext);

  const { trackEvent } = useAnalytics();

  const participantsLength = excludeRecorder(zoomClient?.getAllUser()).length;

  const getPiPParticipants = () => {
    return participants.filter(
      (participant) => participant.userId !== zoomClient?.getSessionInfo()?.userId
    );
  };

  const startPictureInPictureHandler = async () => {
    if (!videoListRef.current) return;

    const otherParticipants = getPiPParticipants();
    const participantId = otherParticipants?.[0]?.userId.toString();
    const container = videoListRef.current[participantId];
    const video = container?.querySelector('video');

    if (!video) {
      const error = Object.assign(
        new Error('Picture-in-Picture is unavailable until the other participant turns on video.'),
        { isExpected: true }
      );

      logger.warn(`PiP unavailable: no video element rendered for participant ${participantId}.`);

      throw error;
    }

    try {
      await video.requestPictureInPicture();
      setParticipantInPiP(participantId);
    } catch (error) {
      logger.error('Error in PiP:', error);

      throw error;
    }
  };

  useEffect(() => {
    if (!zoomClient) return;

    const handleVideoChange = async (payload: { state: 'Active' | 'Inactive'; userId: string }) => {
      if (document.pictureInPictureElement) {
        if (payload.state === 'Inactive') {
          document.exitPictureInPicture();
          setParticipantInPiP(undefined);
        }
      }
    };

    zoomClient.on('video-active-change', handleVideoChange);

    return () => {
      zoomClient.off('video-active-change', handleVideoChange);
    };
  }, [zoomClient, participants, participantsLength]);

  return {
    participantInPiP,
    setParticipantInPiP,
    getPiPParticipants,
    startPictureInPictureHandler,
  };
};

export default usePiP;
